<template>
    <div>
        <v-dialog v-model="showCompetitionDialog" max-width="850px">
            <v-card>
                <dialog-header :title="competition.name" :image="imageUrl" @close="showCompetitionDialog = false" />
                <v-row v-if="loading">
                    <div class="d-flex align-center justify-center">
                        <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
                    </div>
                </v-row>
                <v-row v-else no-gutters>
                    <v-col cols="12" md="5">
                        <TeamsportCompetitionInfo :competition="competition" class="ml-4" />
                    </v-col>

                    <v-col cols="12" md="7">
                        <v-list>
                            <v-list-item-group>
                                <v-list-item v-for="gameClass in computedGameClasses" :key="gameClass.id">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ gameClass.name }}</v-list-item-title>
                                        <v-list-item-subtitle class="wrap-text">
                                            {{ gameClass.subtitle }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action >
                                        <v-btn v-if="registrationHasNotClosed" class="mb-2" small outlined :disabled="gameClass.isDisabled" :color="gameClass.buttonColor" @click="handleClick(gameClass)">
                                            {{ gameClass.buttonText }}
                                        </v-btn>
                                        <v-btn v-if="gameClass.resultBtn.show" small outlined color="primary" @click="goToGameclassOverview(gameClass)">
                                            {{ gameClass.resultBtn.text }}
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </v-col>
                </v-row>
            </v-card>
        </v-dialog>
        <TeamsportBooking 
            v-model="showBookingDialog" 
            :gameClassId="selectedGameClass.id" 
            @close="closeDialog" 
            v-if="showBookingDialog">
        </TeamsportBooking>

        <WatchDialog
            v-model="showWatchDialog"
            :entity="selectedGameClass"
            :type="'gameclass'"
            :infoComponent="TeamsportCompetitionInfo"
            :infoComponentProps="{competition: competition}"
            @close="closeDialog"
            v-if="showWatchDialog">
        </WatchDialog>
    </div>
</template>

<script>
import TeamsportCompetitionInfo from './TeamsportCompetitionInfo.vue';
import DialogHeader from './DialogHeader.vue';
import TeamsportBooking from '../views/TeamsportBooking.vue';
import WatchDialog from '../views/WatchDialog.vue';
export default {
    name: 'zoezi-teamsport-competition-dialog',
    props:{
        competition : {
            type: Object,
            required: true
        },
        value: {
            type: Boolean
        }
    },

    components: {
        TeamsportCompetitionInfo,
        DialogHeader,
        TeamsportBooking,
        WatchDialog,
    },

    watch:{
        value: {
            immediate: true,
            handler(value) {
                this.showCompetitionDialog = value;
            }
        },
        showCompetitionDialog(value) {
            this.$emit('input', value);
        },
    },

    data() {
        return {
            gameClasses: [],
            selectedGameClass: null,
            showBookingDialog: false,
            showCompetitionDialog: false,
            showWatchDialog: false,
            loading: true,
            TeamsportCompetitionInfo: TeamsportCompetitionInfo,
        };
    },

    computed: {
        computedGameClasses() {
            return this.gameClasses.map(gameClass => {
                return {
                    ...gameClass,
                    buttonText: this.getButtonText(gameClass),
                    buttonColor: this.getButtonColor(gameClass),
                    spacesLeft: this.getSpacesLeft(gameClass),
                    isDisabled: this.getButtonDisabled(gameClass),
                    subtitle: this.getSubtitle(gameClass),
                    resultBtn: {
                        text: this.$translate('Results & Schedule'),
                        color: 'primary',
                        show: gameClass.steps.some(step => step.divisions.some(division => division.matches_created))
                    }
                };
            });
        },
        imageUrl() {
            if (!this.competition.teamsport?.imagekey) {
                return this.$api.getImageUrl('/assets/img/teamsport.jpg');
            }
            return this.$api.getImageUrl(this.competition.teamsport?.imagekey)
        },
        registrationHasNotOpened() {
            let today = Date.newFull(this.$store.state.now30s.clone().yyyymmdd())
            let registrationStartDate = Date.newFull(this.competition.registrationStartDate)
            return registrationStartDate > today;
        },
        registrationHasNotClosed() {
            let today = Date.newFull(this.$store.state.now30s.clone().yyyymmdd())
            let registrationEndDate = Date.newFull(this.competition.registrationEndDate)
            return registrationEndDate >= today;
        },
    },

    created() {
        this.getGameClasses();
        this.unsubscribe = this.$api.subscribe({type: 'teamsportgameclass', callback: () => { this.getGameClasses() } });
    },

    destroyed() {
        this.unsubscribe?.();
    },

    methods: {
        goToGameclassOverview(gameClass) {
            const gameClassOverviewPage = this.$root.siteconfig?.pages?.gameClassOverview;
            const param = this.$root.siteconfig.pages.gameClassOverviewParamId;
            const link = `${gameClassOverviewPage}?${param}=${gameClass.id}`;
            this.$root.followLink(link);
        },
        closeDialog() {
            this.showBookingDialog = false;
            this.showWatchDialog = false;
            this.selectedGameClass = null;
        },
        getGameClasses() {
            this.loading = true;
            this.$api.get('/api/teamsport/gameclass/get_for_memberpage', { competition_id: this.competition.id }).then(response => {
                this.gameClasses = response;
                this.loading = false;
            });
        },
        handleClick(gameClass) {
            let now = this.$store.state.now30s
            let registrationStartDate = Date.newFull(gameClass.competition.registrationStartDate)
            if((!gameClass.max_number_of_teams || gameClass.spaceLeft)  && registrationStartDate < now){
                this.selectedGameClass = gameClass;
                this.showBookingDialog = true;
            } else  {
                this.selectedGameClass = gameClass;
                this.showWatchDialog = true;
            }
        },
        getButtonText(gameClass) {
            if (gameClass.individual && gameClass.teams?.find(t => t.user_id == this.$store.state.user?.id) && gameClass.canBookFor == 'self') {
                return this.$translate('Already registered');
            }

            if ((!gameClass.max_number_of_teams || gameClass.spaceLeft)  && !this.registrationHasNotOpened) {
                return this.$translate('Register');
            }

            if (this.isWatching(gameClass)) {
                return this.$translate('Change watching');
            } else {
                return this.$translate('Watch');
            }
        },
        getButtonColor(gameClass) {
            if (this.isWatching(gameClass) && this.registrationHasNotOpened) {
                return 'warning';
            } else {
                return 'primary';
            }
        },
        getSpacesLeft(gameClass) {
            if (!gameClass.max_number_of_teams) {
                return this.$translate('Unlimited');
            }
            return gameClass.spaceLeft ? gameClass.spaceLeft : 0;
        },
        getButtonDisabled(gameClass) {
            return gameClass.individual && gameClass.teams?.find(t => t.user_id == this.$store.state.user?.id) && gameClass.canBookFor == 'self';
        },
        getSubtitle(gameClass) {
            return `${this.$translate("Price")}: ${gameClass.price} | ${this.$translate("Spaces left")}: ${this.getSpacesLeft(gameClass)}`
        },
        isWatching(gameClass) {
            return this.$store.state.bookings?.gameclasswatches?.find(w => w.gameclass_id == gameClass.id && w.user_id == this.$store.state.user?.id);
        }
    }
};
</script>

<style scoped>
.wrap-text {
    white-space: normal;
}
</style>

